/* eslint-disable @typescript-eslint/promise-function-async */
/* eslint-disable @typescript-eslint/no-misused-promises */
import type {
  InputData,
  IntegrationDescription,
} from "@smartsuite/automation-engine-api";
import {
  EmptyButton,
  Icon,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  RoundButton,
  roundButtonStylesMedium,
} from "@smartsuite/react-ui/lib";
import { SolutionColors } from "@smartsuite/types";
import type {
  AuthenticatedConnectUser,
  IntegrationInstallEvent,
} from "@useparagon/connect";
import { t } from "i18next";
import { observer } from "mobx-react-lite";
import { Fragment, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import * as R from "remeda";
import type { AuthAttempt } from "../../../auth";
import { authDispatcher } from "../../../auth";
import type { EngineClient } from "../../../services/engineClient";
import { useEngineClient } from "../../../services/engineClient";
import type { ParagonData, ParagonEvent } from "../../../services/paragon";
import { getParagon } from "../../../services/paragon";
import type { CandidateStore } from "../../../stores/automation/candidate.store";
import type { AutomationsStore } from "../../../stores/automation/index.store";
import { useRootStore } from "../../../stores/hooks";
import type { ActionCandidate } from "../../../types/candidate";
import { requiredInputData } from "../../../utils/form-validations";
import * as AppStyle from "../../App/style";
import { emptyString } from "./empty-string";
import { SelectEditor } from "./SelectEditor";
import * as S from "./style";
import { TextEditor } from "./TextEditor";

export const AuthUnit = observer<{
  updateActionDescription: (
    candidate: CandidateStore,
    action: ActionCandidate,
    integrationList: IntegrationDescription[],
    engineClient: EngineClient,
    automations: AutomationsStore,
  ) => Promise<void>;
  authAttempt: AuthAttempt;
  setAuthAttempt: (auth?: AuthAttempt) => void;
  integrationTitle?: string;
}>(
  ({
    authAttempt,
    setAuthAttempt,
    integrationTitle,
    updateActionDescription,
  }) => {
    const store = useRootStore();
    const engineClient = useEngineClient();

    const actionGroupType =
      store.automations.candidate.currentActionGroup?.config.oneofKind ??
      "actions";

    const [loading, setLoading] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [notConnected, setNotConnected] = useState<boolean>(true);
    const [paragonData, setParagonData] = useState<ParagonData | undefined>(
      undefined,
    );
    const authTokens = new Set<string>([]);

    const {
      automations: { candidate },
      integrations: { list },
      config: { userPreferences },
    } = store;

    const descriptionList =
      authAttempt.authMethod.type.oneofKind === "inputs"
        ? authAttempt.authMethod.type.inputs.inputs
        : [];

    const { control, handleSubmit, formState } = useForm<
      Record<string, InputData>
    >({
      defaultValues: descriptionList.reduce<Record<string, InputData>>(
        (acc, item) => {
          acc[item.inputId] = emptyString(item.inputId);
          return acc;
        },
        {},
      ),
    });

    const onSubmit = (data: Record<string, InputData>): void => {
      setLoading(true);
      void authDispatcher(
        authAttempt,
        Object.values(data),
        store,
        engineClient,
        actionGroupType,
      )
        .then(() => {
          setAuthAttempt();
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
          setErrorMessage(
            t("automation-form.invalid-integration-credentials", {
              label: integrationTitle,
            }),
          );
        });
    };

    const getProviderId = (
      event: ParagonEvent,
      user: AuthenticatedConnectUser,
    ): string => {
      const integration = user.integrations[event.integrationType];

      return integration?.providerId ?? "";
    };

    useEffect(() => {
      if (paragonData === undefined) {
        const action: ActionCandidate | undefined = R.find(
          candidate.allActions,
          (a) => a.hash === authAttempt.target.hash,
        );
        store.automations.setLoadingDynamicDescription(true);
        store.automations.addToLoadingDynamicDescriptionProcess(
          "action",
          action?.actionReference?.instanceId,
        );
        void getParagon(
          authAttempt.authMethod.type.oneofKind === "paragonAuth"
            ? authAttempt.authMethod.type.paragonAuth.authenticateBrowserUrl
            : undefined,
        ).then((data) => {
          setParagonData(data);
          store.automations.setLoadingDynamicDescription(false);
          store.automations.removeFromLoadingDynamicDescriptionProcess(
            "action",
            action?.actionReference?.instanceId,
          );
        });
      }
    }, [paragonData]);

    const connected =
      authAttempt.authMethod.type.oneofKind === "paragonAuth" &&
      paragonData !== undefined &&
      notConnected;

    useEffect(() => {
      if (connected) {
        const action: ActionCandidate | undefined = R.find(
          candidate.allActions,
          (a) => a.hash === authAttempt.target.hash,
        );
        setNotConnected(false);
        paragonData.client.connect(authAttempt.integrationId, {
          overrideRedirectUrl: paragonData.paragonRedirectOauthUrl,
          onClose: () => {
            setAuthAttempt(undefined);
            setTimeout(() => {
              if (action && action.credentialId !== "") {
                store.automations.setLoadingDynamicDescription(true);
                store.automations.addToLoadingDynamicDescriptionProcess(
                  "action",
                  action.actionReference?.instanceId,
                );
                void updateActionDescription(
                  candidate,
                  action,
                  list,
                  engineClient,
                  store.automations,
                )
                  .then(() => {
                    store.automations.setLoadingDynamicDescription(false);
                    store.automations.removeFromLoadingDynamicDescriptionProcess(
                      "action",
                      action?.actionReference?.instanceId,
                    );
                  })
                  .finally(() => {
                    store.automations.setRefreshingList(false);
                  });
              }
            }, 500);
          },
          onSuccess: (
            event: IntegrationInstallEvent,
            user: AuthenticatedConnectUser,
          ) => {
            const paragonUserProviderId = getProviderId(event, user);
            if (authTokens.has(user.token)) {
              return;
            }
            authTokens.add(user.token);
            void authDispatcher(
              authAttempt,
              [],
              store,
              engineClient,
              actionGroupType,
              paragonData.paragonUserId,
              paragonUserProviderId,
            ).then(() => {
              void paragonData.client.setUserMetadata({
                Email: userPreferences?.email ?? "",
                Name: `${userPreferences?.full_name?.first_name ?? ""} ${
                  userPreferences?.full_name?.last_name ?? ""
                }`,
              });
            });
          },
        });
      }
    }, [connected]);

    if (authAttempt.authMethod.type.oneofKind !== "inputs") return null;

    return (
      <ModalOverlay animation="fade" active={true} closeByEsc={false}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <AppStyle.LocalCssVariablesWrapper>
            <Modal
              styles={{ width: 500 }}
              onClose={() => setAuthAttempt()}
              header={
                <ModalHeader
                  title={t("automation-form.auth-unit-modal-header-title", {
                    name: integrationTitle ?? "",
                  })}
                  iconSize="M"
                  iconName={"link"}
                />
              }
              footer={
                <ModalFooter
                  actions={
                    R.isTruthy(errorMessage) && (
                      <S.FooterError>
                        <Icon
                          name={"info-baloon"}
                          size={"M"}
                          color={SolutionColors.basePrimaryRed}
                        />
                        <S.FooterErrorText>{errorMessage}</S.FooterErrorText>
                      </S.FooterError>
                    )
                  }
                  buttons={
                    <>
                      <div style={{ marginRight: "10px" }}>
                        <EmptyButton size="lg" onClick={() => setAuthAttempt()}>
                          {t("general.cancel")}
                        </EmptyButton>
                      </div>
                      <RoundButton
                        type="submit"
                        styles={roundButtonStylesMedium}
                        loading={loading}
                      >
                        {t("general.connect")}
                      </RoundButton>
                    </>
                  }
                ></ModalFooter>
              }
            >
              <S.ModalContent>
                <S.DescriptionList>
                  {descriptionList.map((description, i) => {
                    if (description.editor?.editor.oneofKind === "text") {
                      return (
                        <Controller
                          key={description.inputId}
                          name={description.inputId}
                          rules={{
                            validate: description.editor.required
                              ? { required: requiredInputData }
                              : {},
                          }}
                          control={control}
                          render={({ field: { onChange, onBlur, value } }) => (
                            <>
                              <TextEditor
                                key={`${description.inputId}_editor`}
                                description={description}
                                input={value}
                                hasError={R.isTruthy(
                                  formState.errors[description.inputId]
                                    ?.message,
                                )}
                                onChange={(nextInput) => {
                                  onChange(nextInput);
                                  onBlur();
                                }}
                              />
                              {formState.errors[description.inputId]
                                ?.message && (
                                <S.ErrorMessage>
                                  {
                                    formState.errors[description.inputId]
                                      ?.message
                                  }
                                </S.ErrorMessage>
                              )}
                            </>
                          )}
                        ></Controller>
                      );
                    }
                    if (description.editor?.editor.oneofKind === "select") {
                      return (
                        <Controller
                          key={description.inputId}
                          name={description.inputId}
                          rules={{
                            validate: description.editor.required
                              ? { required: requiredInputData }
                              : {},
                          }}
                          control={control}
                          render={({ field: { onChange, onBlur, value } }) => (
                            <>
                              <SelectEditor
                                key={`${description.inputId}_editor`}
                                description={description}
                                input={value}
                                hasError={R.isTruthy(
                                  formState.errors[description.inputId]
                                    ?.message,
                                )}
                                onChange={(nextInput) => {
                                  onChange(nextInput);
                                  onBlur();
                                }}
                              />
                              {formState.errors[description.inputId]
                                ?.message && (
                                <S.ErrorMessage>
                                  {
                                    formState.errors[description.inputId]
                                      ?.message
                                  }
                                </S.ErrorMessage>
                              )}
                            </>
                          )}
                        ></Controller>
                      );
                    }
                    return <Fragment key={description.inputId} />;
                  })}
                </S.DescriptionList>
              </S.ModalContent>
            </Modal>
          </AppStyle.LocalCssVariablesWrapper>
        </form>
      </ModalOverlay>
    );
  },
);
